// utils/CustomCursor.jsx
import React, { useEffect, useRef, useState } from "react";
import { COLORS } from "../const/colors";

const CustomCursor = () => {
  const cursorOuterRef = useRef(null);
  const cursorInnerRef = useRef(null);
  const cursorRingRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);
  const [isClicking, setIsClicking] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [cursorPosition, setCursorPosition] = useState({ x: 0, y: 0 });
  const [targetPosition, setTargetPosition] = useState({ x: 0, y: 0 });

  // Smooth cursor following with spring physics
  useEffect(() => {
    let animationFrameId;

    const springFactor = 0.15; // Higher = faster follow
    const updateCursorPosition = () => {
      // Calculate spring physics
      const dx = targetPosition.x - cursorPosition.x;
      const dy = targetPosition.y - cursorPosition.y;

      // Update position with spring effect
      setCursorPosition((prev) => ({
        x: prev.x + dx * springFactor,
        y: prev.y + dy * springFactor,
      }));

      // Apply the position to cursor elements
      if (cursorOuterRef.current) {
        cursorOuterRef.current.style.transform = `translate(${cursorPosition.x}px, ${cursorPosition.y}px) translate(-50%, -50%)`;
      }

      if (cursorInnerRef.current) {
        cursorInnerRef.current.style.transform = `translate(${targetPosition.x}px, ${targetPosition.y}px) translate(-50%, -50%)`;
      }

      if (cursorRingRef.current) {
        cursorRingRef.current.style.transform = `translate(${
          cursorPosition.x
        }px, ${cursorPosition.y}px) translate(-50%, -50%) scale(${
          isHovered ? 1.5 : 1
        })`;
      }

      animationFrameId = requestAnimationFrame(updateCursorPosition);
    };

    animationFrameId = requestAnimationFrame(updateCursorPosition);

    return () => {
      cancelAnimationFrame(animationFrameId);
    };
  }, [targetPosition, cursorPosition, isHovered]);

  useEffect(() => {
    const handleMouseMove = (event) => {
      const { clientX, clientY } = event;
      setTargetPosition({ x: clientX, y: clientY });
    };

    document.addEventListener("mousemove", handleMouseMove);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
    };
  }, []);

  useEffect(() => {
    // Handle mouse clicks
    const handleMouseDown = () => setIsClicking(true);
    const handleMouseUp = () => setIsClicking(false);

    // Dynamic interactive element detection
    const handleMouseOver = (e) => {
      const target = e.target;
      if (
        target.tagName === "A" ||
        target.tagName === "BUTTON" ||
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.tagName === "SELECT" ||
        target.classList.contains("clickable") ||
        target.closest(
          'a, button, .clickable, [role="button"], input, textarea, select'
        )
      ) {
        setIsHovered(true);
      } else {
        setIsHovered(false);
      }
    };

    // Check for modal presence
    const checkForModals = () => {
      const modalElements = document.querySelectorAll(
        '[class*="modal"], [class*="Modal"]'
      );
      setIsModalOpen(modalElements.length > 0);
    };

    // Set up mutation observer to detect modal changes
    const observer = new MutationObserver(checkForModals);
    observer.observe(document.body, { childList: true, subtree: true });

    document.addEventListener("mouseover", handleMouseOver);
    document.addEventListener("mousedown", handleMouseDown);
    document.addEventListener("mouseup", handleMouseUp);

    // Initial check
    checkForModals();

    return () => {
      document.removeEventListener("mouseover", handleMouseOver);
      document.removeEventListener("mousedown", handleMouseDown);
      document.removeEventListener("mouseup", handleMouseUp);
      observer.disconnect();
    };
  }, []);

  return (
    <>
      {/* Outer cursor - follows with delay */}
      <div
        ref={cursorOuterRef}
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: isHovered ? "40px" : "30px",
          height: isHovered ? "40px" : "30px",
          borderRadius: "50%",
          backgroundColor: isHovered
            ? `${COLORS.ORANGE}22`
            : `${COLORS.DARK_BLUE}11`,
          pointerEvents: "none",
          zIndex: isModalOpen ? 10000 : 9999,
          transition:
            "width 0.3s cubic-bezier(0.25, 0.8, 0.25, 1), height 0.3s cubic-bezier(0.25, 0.8, 0.25, 1), background-color 0.3s",
          opacity: isModalOpen ? 0.5 : 0.7,
          willChange: "transform",
          transform: "translate(0px, 0px) translate(-50%, -50%)",
          mixBlendMode: "normal",
        }}
      />

      {/* Ring cursor - follows with medium delay */}
      <div
        ref={cursorRingRef}
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: isHovered ? "36px" : "26px",
          height: isHovered ? "36px" : "26px",
          borderRadius: "50%",
          border: isHovered
            ? `2px solid ${COLORS.ORANGE}`
            : `1.5px solid ${COLORS.DARK_BLUE}`,
          borderStyle: isClicking ? "dashed" : "solid",
          pointerEvents: "none",
          zIndex: isModalOpen ? 10001 : 10000,
          transition:
            "width 0.2s, height 0.2s, border 0.2s, opacity 0.3s, transform 0.1s",
          opacity: isModalOpen ? 0.7 : 1,
          willChange: "transform",
          transform: "translate(0px, 0px) translate(-50%, -50%)",
        }}
      />

      {/* Inner cursor - follows immediately */}
      <div
        ref={cursorInnerRef}
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: isClicking ? "6px" : "8px",
          height: isClicking ? "6px" : "8px",
          borderRadius: "50%",
          backgroundColor: isHovered ? COLORS.ORANGE : COLORS.DARK_BLUE,
          pointerEvents: "none",
          zIndex: isModalOpen ? 10002 : 10001,
          transition: "width 0.1s, height 0.1s, background-color 0.3s",
          opacity: isModalOpen ? 0.8 : 1,
          willChange: "transform",
          transform: "translate(0px, 0px) translate(-50%, -50%)",
          boxShadow: `0 0 ${isHovered ? "10px" : "5px"} ${
            isHovered ? COLORS.ORANGE : COLORS.DARK_BLUE
          }88`,
        }}
      />
    </>
  );
};

export default CustomCursor;
